import React, { useCallback, useMemo, useState } from 'react';

import { Confirm, Icons, Modal, PageTitle, Table } from '@components/common';

import { useMutateDeleteAssetSubType } from '@hooks/assets/assetTypes/useMutateDeleteAssetSubType';
import { useMutateDeleteAssetType } from '@hooks/assets/assetTypes/useMutateDeleteAssetType';
import { useQueryAllAssetTypes } from '@hooks/assets/assetTypes/useQueryGetAllAssetTypes';

import { ISortBy, ITableSearch, ITableSort } from '@interfaces/components/table';
import { IManageAssetTypeItem } from '@interfaces/platform/manageAssetTypes';

import colors from '@constants/colors';
import {
	addAssetSubTypeModalTitle,
	addAssetTypeModalTitle,
	addTooltipText,
	deleteAssetSubTypeModalQuestion,
	deleteAssetTypeModalQuestion,
	deleteModalWidth,
	editAssetSubTypeModalTitle,
	editAssetTypeModalTitle,
	emptyTableText,
	manageAssetTypesTitle,
	modalWidth,
	searchTooltipText,
} from './constants';

import AssetSubTypeForm from './AssetSubTypeForm';
import AssetTypeForm from './AssetTypeForm';
import { getColumns } from './columns';

import styles from './ManageAssetTypes.module.scss';

const ManageAssetTypes = () => {
	const [isAddTypeModal, setIsShowAddTypeModal] = useState(false);
	const [isSubTypeModal, setIsSubTypeModal] = useState(false);
	const [isDeleteModal, setIsDeleteModal] = useState(false);
	const [isSubType, setIsSubType] = useState(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);

	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState('');

	const [selectedRow, setSelectedRow] = useState<IManageAssetTypeItem | null>(null);

	const assetTypeModalTitle = selectedRow?.assetTypeName
		? editAssetTypeModalTitle
		: addAssetTypeModalTitle;

	const assetSubTypeModalTitle = selectedRow?.assetSubTypeName
		? editAssetSubTypeModalTitle
		: addAssetSubTypeModalTitle;

	const deleteModalQuestion = isSubType
		? deleteAssetSubTypeModalQuestion
		: deleteAssetTypeModalQuestion;

	const openAssetFormModal = useCallback(() => setIsShowAddTypeModal(true), []);

	const closeAssetFormModal = useCallback(() => {
		setIsShowAddTypeModal(false);
		setSelectedRow(null);
	}, []);

	const openSubTypeModal = useCallback(() => setIsSubTypeModal(true), []);

	const closeSubTypeModal = useCallback(() => {
		setIsSubTypeModal(false);
		setSelectedRow(null);
	}, []);

	const openDeleteModal = useCallback((isSubtype: boolean) => {
		setIsSubType(isSubtype);
		setIsDeleteModal(true);
	}, []);

	const closeDeleteModal = useCallback(() => {
		setIsDeleteModal(false);
		setSelectedRow(null);
	}, []);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const searchHandler = useCallback((searchValue: string) => setSearchValue(searchValue), []);

	const search: Array<ITableSearch> = useMemo(() => ([{
		initialValue: '',
		name: searchTooltipText,
		handler: searchHandler,
		tooltipText: searchTooltipText,
	}]), [searchHandler]);

	const columns = useMemo(() => {
		return getColumns({
			openAssetFormModal,
			openSubTypeModal,
			setSelectedRow,
			openDeleteModal,
		});
	}, [openAssetFormModal, openSubTypeModal, openDeleteModal]);

	const { assetTypes, isLoadingAssetTypes } = useQueryAllAssetTypes({
		searchValue,
		sortDirection,
		sortFieldName,
	});

	const { handleDeleteAssetType, isLoadingDeleteAssetType } = useMutateDeleteAssetType(closeDeleteModal);
	const { handleDeleteAssetSubType, isLoadingDeleteAssetSubType } = useMutateDeleteAssetSubType(closeDeleteModal);

	const deleteHandler = useCallback(() => {
		if (!selectedRow?.id) return;

		if (isSubType) {
			handleDeleteAssetSubType(selectedRow?.id);
		} else {
			handleDeleteAssetType(selectedRow?.id);
		}
	}, [handleDeleteAssetSubType, handleDeleteAssetType, isSubType, selectedRow?.id]);

	return (
		<div className={styles.assetTypesContainer}>
			<PageTitle
				icon={Icons.layer({ color: colors.white })}
				title={manageAssetTypesTitle}
			/>
			<Modal
				isOpen={isDeleteModal}
				onClose={closeDeleteModal}
				width={deleteModalWidth}
			>
				<Confirm
					isLoading={isLoadingDeleteAssetType || isLoadingDeleteAssetSubType}
					onClose={closeDeleteModal}
					onSubmit={deleteHandler}
					question={deleteModalQuestion}
				/>
			</Modal>

			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyFormModal}
				isOpen={isSubTypeModal}
				onClose={closeSubTypeModal}
				title={assetSubTypeModalTitle}
				width={modalWidth}
			>
				<AssetSubTypeForm
					assetSubTypeName={selectedRow?.assetSubTypeName}
					assetTypeName={selectedRow?.assetTypeName}
					closeFormModal={closeSubTypeModal}
					id={selectedRow?.id}
					onClose={closeSubTypeModal}
					setIsDirtyFormModal={setIsDirtyFormModal}
				/>
			</Modal>

			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyFormModal}
				isOpen={isAddTypeModal}
				onClose={closeAssetFormModal}
				title={assetTypeModalTitle}
				width={modalWidth}
			>
				<AssetTypeForm
					assetIconId={selectedRow?.icon?.id}
					assetTypeName={selectedRow?.assetTypeName}
					closeFormModal={closeAssetFormModal}
					id={selectedRow?.id}
					isWithSubType={!!selectedRow?.subTypes?.length}
					onClose={closeAssetFormModal}
					selectedFields={selectedRow?.requiredSystemFieldNames}
					setIsDirtyFormModal={setIsDirtyFormModal}
				/>
			</Modal>
			<div className={styles.assetTypesTableContainer}>
				<Table
					addTooltipText={addTooltipText}
					columns={columns}
					data={assetTypes}
					emptyText={emptyTableText}
					isCreatable
					isEmptyTextCenter
					isLoading={isLoadingAssetTypes}
					isWithPagination
					isWithoutActions
					isWithoutCheckbox
					isWithoutDeleteAction
					isWithoutFilter
					search={search}
					sort={sort}
					toggleAddModal={openAssetFormModal}
				/>
			</div>
		</div>
	);
};

export default ManageAssetTypes;
